<template>
    <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="dataTableFooterProps"
        :loading="isBusy"
    >
        <template v-slot:top>
            <v-toolbar flat color="white">
                <v-text-field
                    v-model="filter"
                    prepend-inner-icon="mdi-magnify"
                    :placeholder="$t('common.filter')"
                    outlined
                    dense
                    hide-details
                />
            </v-toolbar>
        </template>
        <template v-slot:item.external_id="{ item }">
            {{ item.external_id ? item.external_id : '-' }}
        </template>
        <template v-slot:item.state="{ item }">
            <v-chip-group column>
                <v-chip
                    v-for="(external_order_state, i) in item.external_order_states"
                    :key="i"
                    :color="stateColor(external_order_state)"
                    :ripple="false"
                    small
                >
                    {{ $t("orders.external_order_states." + external_order_state) }}
                </v-chip>
            </v-chip-group>
        </template>
        <template v-slot:item.reservation_status="{ item }">
            <v-tooltip bottom :disabled="!orderErrorMessage(item)">
                <template v-slot:activator="{ on, attrs }">
                    <v-chip
                        v-bind="attrs"
                        v-on="on"
                        :color="reservationStatusColor(item.reservation_status)"
                        :ripple="false"
                        small
                    >
                        <v-icon v-if="item.provider_error_message" left small>mdi-alert-circle</v-icon>
                        {{ reservationStatusLabel(item.reservation_status) }}
                    </v-chip>
                </template>
                <span>{{ orderErrorMessage(item) }}</span>
            </v-tooltip>
        </template>
        <template v-slot:item.created_at="{ item }">
            <span class="text-no-wrap">
                {{ $moment(item.created_at).format('LLL') }}
            </span>
        </template>
        <template v-slot:item.updated_at="{ item }">
            <span class="text-no-wrap">
                {{ item.updated_at ? $moment(item.updated_at).format('LLL') : '-' }}
            </span>
        </template>
        <template v-slot:item.email="{ item }">
            {{ item.address.email ? item.address.email : '-' }}
        </template>
        <template v-slot:item.phone="{ item }">
            <span class="text-no-wrap">
                {{ item.address.phone ? item.address.phone : '-' }}
            </span>
        </template>
        <template v-slot:item.recipient="{ item }">
            {{ addressString(item.address) }}
        </template>
        <template v-slot:item.message="{ item }">
            {{ item.message ? item.message : '-' }}
        </template>
        <template v-slot:item.part_of_bulk_order="{ item }">
            {{ item.bulk_order_id !== null ? $t('common.yes') : $t('common.no') }}
        </template>
        <template v-slot:item.actions="{ item }" v-if="!hideActions">
            <div class="text-no-wrap">
                <v-btn v-if="item.bulk_order_id === null" @click="onDetail(item)" :disabled="isBusy" icon>
                    <v-icon>mdi-magnify</v-icon>
                </v-btn>
                <v-btn v-if="isApprover && item.bulk_order_id === null" @click="onEdit(item)" :disabled="isBusy || item.state != 0 || isOrderActionLocked(item)" icon>
                    <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn v-if="item.bulk_order_id === null" :disabled="isBusy || item.state != 0 || isOrderActionLocked(item)" @click="onDelete(item)" color="error" icon>
                    <v-icon>mdi-trash-can</v-icon>
                </v-btn>
            </div>
        </template>
    </v-data-table>
</template>

<script>
import {API} from '@/api/api'
import SnackbarMixin from '@/mixins/SnackbarMixin'
import DataTableMixin from '@/mixins/DataTableMixin'
import _ from 'lodash'

export default {
    name: "OrdersTable",
    mixins: [SnackbarMixin, DataTableMixin],
    props: {
        url: {
            type: String,
            required: true
        },
        hideActions: {
            type: Boolean,
            required: false,
            default: () => false
        },
        includingOrdersFromBulkOrder: {
            type: Boolean,
            required: false,
            default: () => true
        },
        includingApproved: {
            type: Boolean,
            required: false,
            default: () => false
        }
    },
    data: () => ({
        // loading
        isBusyOrders: false,
        isBusyDelete: false,
        // pagination metadata
        total: 0,
        // data table items
        items: [],
        // table filter
        filter: "",
        // data table options
        options: {
            page: 1,
            itemsPerPage: 15,
            sortBy: ["created_at"],
            sortDesc: [true]
        }
    }),
    watch: {
        options: {
            handler() {
                this.fetchOrders()
            },
            deep: true
        },
        includingOrdersFromBulkOrder: {
            handler() {
                this.fetchOrders()
            },
            deep: true
        },
        filter: {
            handler: _.debounce(function () {
                this.resetServerTablePage(this.fetchOrders);
            }, 250)
        }
    },
    computed: {
        isBusy() {
            return this.isBusyOrders || this.isBusyDelete;
        },
        headers() {
            const headers = [
                {
                    text: '#',
                    value: 'external_id'
                },
                {
                    text: this.$t('orders.state'),
                    value: 'state'
                },
                {
                    text: this.$t('orders.reservation_status.reservation_status'),
                    value: 'reservation_status',
                    sortable: false
                },
                {
                    text: this.$t('orders.created_at'),
                    value: 'created_at'
                },
                {
                    text: this.$t('orders.updated_at'),
                    value: 'updated_at'
                },
                {
                    text: this.$t('orders.recipient_email'),
                    value: 'email',
                    sortable: false
                },
                {
                    text: this.$t('orders.recipient_phone'),
                    value: 'phone',
                    sortable: false
                },
                {
                    text: this.$t('orders.recipient_address'),
                    value: 'recipient',
                    sortable: false
                },
                {
                    text: this.$t('orders.message'),
                    value: 'message',
                    sortable: false
                },
                {
                    text: this.$t('orders.part_of_bulk_order'),
                    value: 'part_of_bulk_order',
                    sortable: false
                }
            ];
            if (!this.hideActions) {
                headers.push({
                    text: '',
                    value: 'actions',
                    sortable: false,
                    align: 'end'
                })
            }
            return headers;
        },
        isApprover() {
            let roles = this.$store.getters['identity/getRoles'];
            return roles && roles.includes('ROLE_APPROVER');
        }
    },
    methods: {
        fetchOrders() {
            const {sortBy, sortDesc, page, itemsPerPage} = this.options;
            const sortMap = {
                external_id: "externalId",
                created_at: "createdAt",
                updated_at: "updatedAt"
            };
            const url = this.url
                + "?page=" + (page ?? 1)
                + "&itemsPerPage=" + (itemsPerPage ?? 10)
                + "&sortBy=" + this.mapServerSort(sortBy, sortMap, "createdAt")
                + "&sortDesc=" + (_.isArray(sortDesc) ? (sortDesc[0] ? "true" : "false") : "false")
                + "&includingOrdersFromBulkOrder=" + (this.includingOrdersFromBulkOrder ? "true" : "false")
                + "&includingApproved=" + (this.includingApproved ? "true" : "false")
                + (this.filter ? "&filter=" + encodeURIComponent(this.filter) : "");
            this.isBusyOrders = true;
            API.get(url)
                .then(response => {
                    this.total = response.data.total;
                    this.items = response.data.items;
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyOrders = false;
                });
        },
        onDetail(item) {
            this.$router.push({name: 'order', params: {orderId: item.id}});
        },
        onEdit(item) {
            this.$router.push({name: 'edit-order', params: {orderId: item.id}})
        },
        onDelete(item) {
            if (!confirm(this.$t('confirm.delete_order'))) {
                return;
            }
            this.isBusyDelete = true;
            API.delete("/api/orders/" + item.id)
                .then(() => {
                    this.fetchOrders();
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyDelete = false;
                });
        },
        stateColor(externalOrderState) {
            switch (externalOrderState) {
                case "EO_WAITING_FOR_APPROVAL":
                    return "warning";
                case "EO_STATE_CREATED":
                    return "info";
                case "EO_STATE_UNABLE_TO_PROCESS":
                    return "error";
                case "EO_STATE_IN_PROGRESS":
                    return "warning";
                case "EO_STATE_WAITING":
                    return "warning";
                case "EO_STATE_STOCK_PICKING_WAITING":
                    return "warning";
                case "EO_STATE_STOCK_PICKING_IN_PROGRESS":
                    return "warning";
                case "EO_STATE_STOCK_PICKING_COMPLETED":
                    return "success";
                case "EO_STATE_STOCK_PICKING_CANCELED":
                    return "secondary";
                case "EO_STATE_PREPARE_PACKAGE_WAITING":
                    return "warning";
                case "EO_STATE_PREPARE_PACKAGE_IN_PROGRESS":
                    return "warning";
                case "EO_STATE_PREPARE_PACKAGE_COMPLETED":
                    return "success";
                case "EO_STATE_PREPARE_PACKAGE_CANCELED":
                    return "secondary";
                case "EO_STATE_SHIPMENTS_WAITING":
                    return "warning";
                case "EO_STATE_SHIPMENT_CANCELLED":
                    return "secondary";
                case "EO_STATE_SHIPPED":
                    return "success";
                case "EO_STATE_SENT":
                    return "success";
                case "EO_STATE_BEING_DELIVERED":
                    return "success";
                case "EO_STATE_DELIVERED_TO_PICKUP_LOCATION":
                    return "success";
                case "EO_STATE_LOST":
                    return "error";
                case "EO_STATE_DELIVERED":
                    return "success";
                case "EO_STATE_RETURNING":
                    return "warning";
                case "EO_STATE_RETURNED":
                    return "secondary";
                case "EO_STATE_CANCELLED":
                    return "secondary";
                case "EO_STATE_CLOSED":
                    return "success";
                default:
                    return "secondary";
            }
        },
        reservationStatusLabel(status) {
            return this.$t('orders.reservation_status.' + (status || 'unknown'));
        },
        reservationStatusColor(status) {
            switch (status) {
                case "active":
                    return "success";
                case "pending":
                case "cancel_pending":
                    return "warning";
                case "failed":
                    return "error";
                case "cancelled":
                    return "info";
                case "not_required":
                    return "secondary";
                default:
                    return "secondary";
            }
        },
        isOrderActionLocked(item) {
            return ["pending", "cancel_pending"].includes(item.reservation_status);
        },
        orderErrorMessage(item) {
            if (item.provider_error_message) {
                return item.provider_error_message;
            }
            if (item.reservation_status === 'failed') {
                return item.reservation_error_message;
            }
            return null;
        },
        addressString(address) {
            let addressFields = [
                address.name ? address.name.trim() : null,
                address.ico ? address.ico.trim() : null,
                address.dic ? address.dic.trim() : null,
                address.phone ? address.phone.trim() : null,
                address.email ? address.email.trim() : null,
                address.street ? address.street.trim() : null,
                address.house_number ? address.house_number.trim() : null,
                address.suburb ? address.suburb.trim() : null,
                address.city ? address.city.trim() : null,
                address.postal_code ? address.postal_code.trim() : null,
                address.country ? address.country.trim() : null,
                address.country_iso_code ? address.country_iso_code.trim() : null,
            ];
            addressFields = addressFields.filter(x => x && x !== 'null');
            return addressFields.join(", ");
        }
    },
    created() {
        this.fetchOrders();
    }
}
</script>
